import React from 'react';
import { useTranslation } from 'react-i18next';
import FadeInSection from './FadeInSection';

const HeroSection: React.FC = () => {
  const { t } = useTranslation('website');

  return (
    <section id="hero" className="relative min-h-screen flex items-center hero-glow">
      <div className="absolute inset-0 tech-grid opacity-30" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left content */}
          <FadeInSection>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 mb-8">
              <i data-lucide="sparkles" className="text-primary-400 text-sm" />
              <span className="text-primary-300 text-sm font-medium">{t('hero.badge')}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              {t('hero.title')}<br />
              <span className="gradient-text">{t('hero.titleAccent')}</span>
            </h1>
            <p className="text-lg text-dark-400 leading-relaxed mb-10 max-w-xl">
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#business"
                className="inline-flex items-center gap-2 px-8 py-4 bg-primary-600 hover:bg-primary-500 text-white font-semibold rounded-2xl transition-all shadow-lg shadow-primary-600/25"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById('business');
                  if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' });
                }}
              >
                {t('hero.exploreBtn')} <i data-lucide="arrow-down" className="text-sm" />
              </a>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 px-8 py-4 border border-dark-700 hover:border-primary-500/50 text-dark-200 hover:text-white rounded-2xl transition-all"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById('contact');
                  if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' });
                }}
              >
                <i data-lucide="mail" className="text-sm" /> {t('hero.contactBtn')}
              </a>
            </div>
          </FadeInSection>

          {/* Right illustration */}
          <FadeInSection delay="fade-in-delay-1" className="relative hidden lg:block">
            <div className="relative w-full aspect-[4/3] max-w-lg mx-auto animate-float">
              <div className="absolute inset-0 rounded-3xl bg-dark-900/80 border border-dark-700/50 flex items-center justify-center overflow-hidden">
                <img
                  src="/images/hero-illustration.png"
                  alt="AI Technology"
                  className="w-full h-full object-cover rounded-3xl"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                    const fallback = (e.target as HTMLImageElement).nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
                <div className="hidden flex-col items-center gap-4 text-dark-500">
                  <i data-lucide="brain" className="text-6xl text-primary-500/40" />
                  <span className="text-sm">Hero Illustration</span>
                </div>
              </div>
              <div className="absolute -inset-1 bg-gradient-to-br from-primary-500/20 via-accent-500/10 to-purple-500/10 rounded-3xl -z-10 blur-xl" />
            </div>
          </FadeInSection>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mt-12">
          {[
            { num: t('hero.stat1Num'), label: t('hero.stat1Label') },
            { num: t('hero.stat2Num'), label: t('hero.stat2Label') },
            { num: t('hero.stat3Num'), label: t('hero.stat3Label') },
            { num: t('hero.stat4Num'), label: t('hero.stat4Label') },
          ].map((stat, i) => (
            <FadeInSection key={i} delay={`fade-in-delay-${i + 1}` as any} className="text-center p-5 sm:p-6 rounded-2xl bg-dark-900/40 border border-dark-800/50 hover:border-primary-500/30 transition-colors">
              <div className="text-3xl font-bold stat-number mb-1">{stat.num}</div>
              <div className="text-sm text-dark-400">{stat.label}</div>
            </FadeInSection>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
